import * as XLSX from 'xlsx';
import Formatter from 'utils/Formatter';
import * as moment from 'moment';

const paymentMethod = {
  CARD: '신용/체크카드',
  TRANSFER: '계좌이체',
  VIRTUAL_ACCOUNT: '가상계좌',
  // DIRECT_DEPOSIT: '무통장입금',
  MOBILE: '휴대폰결제',
  TOSS_PAY: '토스페이',
  CASH: '현금',
  NAVER_PAY: '네이버페이',
  KAKAO_PAY: '카카오페이',
  APPLE_PAY: '애플페이',
  SAMSUNG_PAY: '삼성페이',
  POINT: '포인트',
  GIFT_CARD: '기프트카드',
  PAYPAL: '페이팔',
  INSTALLMENT: '할부',
  ALI_PAY: '알리페이',
  WECHAT_PAY: '위챗페이',
  COOP: '쿠프',
  INFI: '스마트인피니',
  INFI_OPTIONAL: '스마트인피니 현금영수증',
  PAYS: '페이즈',
  PCI_PAY: '페이코인',
  RHEA_PAY: '레아페이',
  ZERO_PAY: '제로페이',
  FUN: '즐거운',
  CJONE_PAY: '씨제이원페이',
  VENDYS: '식권대장',
  GIFTI_SHOW: 'KT 기프티쇼',
  PAYCO: '페이코',
  KONPAY: '콘페이',
  SMARTCON: '스마트콘',
  SEOUL_PAY: '서울페이',
  SSG_PAY: '신세계페이',
  L_PAY: '롯데페이',
  JM_PAY: '제이엠페이',
  PRESENT: '선물하기',
};
const purchaseStatus = {
  PENDING: '구매확정전',
  CONFIRMED: '구매확정',
  RETURNED: '반품',
  CALCULATE: '정산완료',
  COMPLETED: '완료',
  PARTIALLY_REFUNDED: '환불',
  REFUNDED: '환불',
};
const productType = {
  DELIVERY: '배송상품',
  RENTAL: '렌탈상품',
  SUBSCRIPTION: '정기구독상품',
};
const userStatus = {
  ACTIVE: '정상',
  INACTIVE: '비활성',
  DORMANT: '휴면',
  WITHDRAWN: '탈퇴',
};
const userSignUpType = {
  MEMBER: '일반가입',
  NAVER: '네이버',
  GOOGLE: '구글',
  APPLE: '애플',
  KAKAO: '카카오',
  NONE: '비회원',
};
const userType = {
  ALL: '전체',
  USER: '회원',
  ADMIN: '관리자',
  SHOP_ADMIN: '쇼핑몰관리자',
  STORE_ADMIN: '매장관리자',
  VD: '포스',
  KIOSK: '키오스크',
  GUEST: '게스트',
};
const applicationStatus = {
  WAIT: '대기',
  APPLY: '신청',
  REJECT: '반려',
  APPROVE: '승인',
  DISABLED: '계정비활성화',
};
const menuType = {
  GENERAL: '메뉴',
  MD: 'MD',
  DESSERT: '디저트',
  ALC: '주류',
  ETC: '기타',
};
const productStatus = {
  SOLDOUT: '품절',
  NORMAL: '정상',
  END: '종료',
  STOP: '중지',
};
const orderRoute = {
  APP: '앱',
  POS: '포스',
};
const orderStatus = {
  READY: '준비전',
  PREPARING: '준비중',
  CANCELLED: '취소',
};

const inquirySubType = {
  INQUIRY_SERVICE: '서비스',
  INQUIRY_USER: '회원정보',
  INQUIRY_JM_PAY: '제이엠페이',
  INQUIRY_JM_PAY_REFUND: '환불신청',
  INQUIRY_DELIVERY: '배송',
  INQUIRY_BULK: '대량문의',
  INQUIRY_ORDER: '주문/결제',
  INQUIRY_ETC: '기타',
  INQUIRY_STORE: '매장',
  INQUIRY_RESTOCK: '재입고',
  INQUIRY_DETAIL: '상세',
};

/**
 * 변수 한글 치환
 * --
 * @param {*} value : string
 * @returns
 */
const replaceValue = (value) => {
  const replaceValues = {
    //회원가입
    ...userSignUpType,
    //회원 상태
    ...userStatus,
    //상품
    ...productType,
    //메소드
    ...paymentMethod,
    //구매상태
    ...purchaseStatus,
    //권한, 인증 구분
    ...userType,
    //신청 상태
    ...applicationStatus,
    //메뉴 타입
    ...menuType,
    //상품 상태
    ...productStatus,
    //결제 주회
    ...orderRoute,
    //주문 상태
    ...orderStatus,
    //문의 유형
    ...inquirySubType,
  };
  return replaceValues[value] ?? value;
};

/**
 * 엑셀 다운로드 함수
 * @param columns - 테이블 컬럼 정의 배열
 * @param rows - 테이블 데이터 배열
 * @param sheetName - 시트명
 * @param fileName - 저장할 파일명
 */
export const downloadXlsx = ({
  columns = [],
  rows = [],
  sheetName = 'Sheet1',
  fileName = 'data',
}) => {
  if (columns?.length === 0) return;
  // 1️⃣ 헤더(title) 생성
  const header = [
    '순번',
    ...columns
      .map((col) => {
        if (col?.title?.toUpperCase() === 'NO') return 'id';
        if (!col?.dataIndex && !col?.key) return null;
        return col.title;
      })
      ?.filter((col) => col !== null),
  ];
  // return;
  // 2️⃣ 데이터 행 생성
  const data = rows.map((row, index) => {
    const rowData = columns.map((col) => {
      const key = col.dataIndex || col.key;
      const value = row[key];
      // 📌 날짜 관련 키인지 확인
      const isDateKey = [
        'day',
        'date',
        'time',
        'created_at',
        'updated_at',
        'created',
        'updated',
      ].some((dateKey) => key?.includes(dateKey));

      // 📌 숫자인 경우 날짜 변환
      if (isDateKey && typeof value === 'number') {
        return value ? Formatter.unixToDate(value, 'YYYY-MM-DD HH:mm:ss') : '-';
      }

      return replaceValue(value) ?? '-';
    });

    return [index + 1, ...rowData];
  });

  // 3️⃣ 워크시트 & 엑셀 파일 생성
  const worksheet = XLSX.utils.aoa_to_sheet([header, ...data]);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(
    workbook,
    `${fileName}_${moment().format('YYYYMMDDHHmmss')}.xlsx`,
  );
};
